// Framework-neutral browser session/event helper for Search Analytics (Online Store).
// Dependency-free ESM. See docs/specs/2026-07-14-search-analytics-design.md.
//
// Storage contract: one namespaced key holds only UUIDs, the canonical criteria
// key, attribution, and last-activity timestamp — no identity, no URLs, no
// timestamps beyond session bookkeeping.

const STORAGE_KEY = "captcan_search_analytics_v1";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes inactivity → new session
const DEFAULT_ENDPOINT = "/apps/captcan/analytics";
// fields that never change what a "search" means (pagination/display, not criteria)
const NON_MEANINGFUL_KEYS = new Set(["page", "pageSize", "view", "compact", "in-stock-only", "sort"]);
// Free-text fields the server validates as shortText (trim, 1..128). Normalize client-side
// so an oversized/whitespace-only value degrades that one field instead of 400-ing the event.
const FREE_TEXT_KEYS = ["query", "publisher", "number", "seriesName", "productTitle"];
const MAX_FREE_TEXT = 128;

function normalizeFreeText(event) {
  const out = { ...event };
  for (const k of FREE_TEXT_KEYS) {
    if (typeof out[k] !== "string") continue;
    const trimmed = out[k].trim().slice(0, MAX_FREE_TEXT);
    if (trimmed) out[k] = trimmed;
    else delete out[k]; // empty/whitespace-only: omit (all these fields are optional)
  }
  return out;
}

function randomUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC4122-ish v4 fallback for environments without crypto.randomUUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Stable, order-independent key over meaningful criteria only.
function canonicalCriteriaKey(criteria) {
  const entries = Object.keys(criteria || {})
    .filter((k) => !NON_MEANINGFUL_KEYS.has(k))
    .map((k) => [k, criteria[k]])
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => [k, typeof v === "string" ? v.trim().toLowerCase() : v])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return JSON.stringify(entries);
}

function loadState(storage, nowMs) {
  let raw = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    raw = null;
  }
  let state = null;
  if (raw) {
    try {
      state = JSON.parse(raw);
    } catch {
      state = null;
    }
  }
  const expired = !state || typeof state.lastActivity !== "number" || nowMs - state.lastActivity > SESSION_TTL_MS;
  if (expired) {
    return {
      sessionId: randomUUID(),
      lastActivity: nowMs,
      searchId: null,
      criteriaKey: null,
      attribution: null,
    };
  }
  return state;
}

function saveState(storage, state) {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable (private mode, quota) — analytics is best-effort only
  }
}

export function createSearchAnalytics(options) {
  const opts = options || {};
  // sessionStorage (not localStorage): the 30-min anonymous session must not survive a
  // browser close, else consecutive shoppers on a shared/kiosk browser merge into one
  // sessionId. Mirrors the Tapcart emitter's defaultStorage().
  const storage =
    typeof window !== "undefined"
      ? opts.storage || window.sessionStorage || window.localStorage
      : opts.storage;
  const now = opts.now || (() => Date.now());
  const endpoint = opts.endpoint || DEFAULT_ENDPOINT;
  const transport = opts.transport || defaultTransport(endpoint);
  const surface = opts.surface || "ONLINE_STORE";

  function send(event) {
    try {
      transport({ ...normalizeFreeText(event), id: randomUUID() });
    } catch {
      // transport failure must never propagate into caller (search/nav/cart/wishlist)
    }
  }

  function completeSearch(criteria, resultCount) {
    const nowMs = now();
    const state = loadState(storage, nowMs);
    const criteriaKey = canonicalCriteriaKey(criteria);
    const isDuplicate = state.searchId && state.criteriaKey === criteriaKey;

    if (!isDuplicate) {
      state.searchId = randomUUID();
      state.criteriaKey = criteriaKey;
      state.attribution = { origin: "SEARCH", searchId: state.searchId };
    }
    state.lastActivity = nowMs;
    saveState(storage, state);

    if (!isDuplicate) {
      send({
        type: "SEARCH_COMPLETED",
        surface,
        origin: "SEARCH",
        sessionId: state.sessionId,
        searchId: state.searchId,
        resultCount,
        ...criteriaFields(criteria),
      });
    }

    return state.searchId;
  }

  function criteriaFields(criteria) {
    const out = {};
    if (criteria.query) out.query = criteria.query;
    if (criteria.lookupId) out.lookupId = Number(criteria.lookupId);
    if (criteria.publisher) out.publisher = criteria.publisher;
    if (criteria.number) out.number = criteria.number;
    if (criteria.yearFrom) out.yearFrom = Number(criteria.yearFrom);
    if (criteria.yearTo) out.yearTo = Number(criteria.yearTo);
    if (criteria.country) out.country = criteria.country;
    return out;
  }

  function track(type, dimensions) {
    const nowMs = now();
    const state = loadState(storage, nowMs);
    const eventDimensions = { ...(dimensions || {}) };
    const explicitOrigin = eventDimensions.origin;
    delete eventDimensions.origin;
    let attribution = state.attribution || { origin: "DIRECT" };

    if (explicitOrigin === "DIRECT") attribution = { origin: "DIRECT" };

    // navigation into a series/product establishes/refreshes attribution for downstream events
    if (type === "SERIES_OPENED" || type === "PRODUCT_OPENED") {
      state.attribution = {
        ...attribution,
        seriesId: eventDimensions.seriesId !== undefined ? eventDimensions.seriesId : attribution.seriesId,
        issueId: eventDimensions.issueId !== undefined ? eventDimensions.issueId : attribution.issueId,
      };
    }
    state.lastActivity = nowMs;
    saveState(storage, state);

    const event = {
      type,
      surface,
      origin: state.attribution ? state.attribution.origin : "DIRECT",
      sessionId: state.sessionId,
      ...eventDimensions,
    };
    if (type !== "COLLECTION_EVENT" && state.attribution && state.attribution.searchId) {
      event.searchId = state.attribution.searchId;
    }
    send(event);
  }

  function getAttribution() {
    const nowMs = now();
    const state = loadState(storage, nowMs);
    if (!state.attribution) return null;
    return { ...state.attribution };
  }

  return { completeSearch, track, getAttribution };
}

function defaultTransport(endpoint) {
  return (event) => {
    const body = JSON.stringify(event);
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(endpoint, blob);
      if (ok) return;
    }
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  };
}

if (typeof window !== "undefined") {
  window.CAPTCAN_SEARCH_ANALYTICS = createSearchAnalytics({});
  window.dispatchEvent(new CustomEvent("captcan:search-analytics-ready"));
}
